import { Box, Stack } from "@mui/material";

import VirtualizedList, { VirtualizedListItem } from "../../virtualized-list/VirtualizedList";
import { AlarmSetpointUsageListItem } from "./AlarmSetpointUsageListItem";
import { SetpointFormType, SetpointUsageActions } from "../AlarmSetpointEditorUtils";
import { useFieldArray, useFormContext } from "react-hook-form";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import SideSheet from "../../side-sheet/side-sheet/SideSheet";
import { useTranslation } from "react-i18next";
import { isMobile, isTablet } from 'react-device-detect';
import SideSheetSwitch from "../../side-sheet/side-sheet-switch/SideSheetSwitch";
import { getObjectTypeName } from "../../../common/utilities/GetObjectTypeName";
import { UseAlarmSetpointUsageStateType } from "./AlarmSetpointUsageState";
import SideSheetFilterInput from "../../side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import { Ref, forwardRef, useEffect, useImperativeHandle, useMemo } from "react";
// import AlarmSetpointUsageUtils from "./AlarmSetpointUsageUtils";
import NoData from "../../no-data/NoData";
import { ViewOptionStateEnum } from "../../contextual-app-bar/ViewOptionStateEnum";
import { BaseActionEnum } from "../../action-menu/ActionMenuItem";
import { IEditTagDTO, IEditTagSearchResultDeviceDTO, IEditTagSearchResultTagDTO } from "../../../services/setpoint-administration-service/Models";
import Enumerable from "linq";
import { leftOuterJoin } from "../../../common/utilities/LinqExtensions";
import { containsOrExactlyMatches } from "../../../common/utilities/ContainsOrExactlyMatches";


export type AlarmSetpointUsageListType = {
    executeAction: (action: SetpointUsageActions | BaseActionEnum) => void;
};

//export type AlarmSetpointUsageItem = FieldArrayWithId<SetpointFormType, 'devices' | 'wells' | 'tanks' | 'tankBatteries', 'fieldKey'> & VirtualizedListItem;
export type AlarmSetpointUsageItem = IEditTagSearchResultDeviceDTO & VirtualizedListItem;

export type AlarmSetpointUsageListProps = {
    name: "deviceTags" | "wellTags" | "tankTags" | "tankBatteryTags";
    visible?: boolean;
    objectType: eLynxObjectTypeEnum;
    openSideSheet?: boolean;
    onCloseSideSheet?: () => void;
    setViewOptionState?: (value: ViewOptionStateEnum) => void;
    setpointUsageState: UseAlarmSetpointUsageStateType;
    usageObjects?: IEditTagSearchResultDeviceDTO[];
};

function AlarmSetpointUsageList({
    name,
    visible,
    objectType,
    openSideSheet,
    onCloseSideSheet,
    setViewOptionState,
    setpointUsageState,
    usageObjects
}: AlarmSetpointUsageListProps, ref: Ref<unknown> | undefined) {

    const isPhone = isMobile && !isTablet;
    const { t } = useTranslation();


    useImperativeHandle<unknown, AlarmSetpointUsageListType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );


    // const {
    //     state, 
    //     setViewSelected,
    //     setObjectFilter,
    //     setTagFilter,
    //     resetState
    // } = useAlarmSetpointUsageState();
    const {
        state: { 
            expandedObjects,
            ...state 
        },
        setViewSelected,
        setObjectFilter,
        setTagFilter,
        resetState,
        setExpandedObjects
    } = setpointUsageState;

    const {
        // getValues,
        // watch,
        control,
        //reset,
        // setValue,
        // trigger

        //resetField
    } = useFormContext<SetpointFormType>();

    const {
        fields,
        append,
        remove,
        replace
    } = useFieldArray({
        control,
        name: name,
        keyName: 'fieldKey'
    });

    const availableObjects = useMemo(() => usageObjects?.map(usageObject => {

        const tags =
            leftOuterJoin(
                usageObject.tags,
                fields,
                tag => tag.id,
                tagField => tagField.tagID,
                (tag, tagField) => ({
                    ...tag,
                    selected: tagField !== null
                })
            ).toArray();

        return {
            ...usageObject,
            tags: tags,
            expanded: expandedObjects.some(o => o.id === usageObject.id)
        };
    }) ?? [], [
        usageObjects,
        fields,
        expandedObjects
    ]);

    const hasTagFilter = state.tagNameFilter.trim().length > 0;

    const filteredObjects = availableObjects
        .filter(
            o =>
                containsOrExactlyMatches(o.name, state.objectNameFilter) &&
                (!hasTagFilter || o.tags.some(t => containsOrExactlyMatches(t.name, state.tagNameFilter))) &&
                (!state.viewSelected || o.tags.some(t => t.selected === true))
        )
        .map(o => ({
            ...o,
            tags: o.tags.filter(
                t =>
                    containsOrExactlyMatches(t.name, state.tagNameFilter) && (!state.viewSelected || t.selected === true)
            ),
            selectedTagsCount: o.tags.filter(t => t.selected).length
        })) ?? [];

    function toggleExpansion(obj: IEditTagSearchResultDeviceDTO) {
        const expanded = expandedObjects.some(o => o.id === obj.id);
        if (expanded) {
            setExpandedObjects(expandedObjects.filter(o => o.id !== obj.id));
        } else {
            setExpandedObjects([...expandedObjects, obj]);
        }
    };

    function expandAll() {
        setExpandedObjects(availableObjects);
    }

    function collapseAll() {
        setExpandedObjects([]);
    }

    // const name = objectType === eLynxObjectTypeEnum.Device ?
    //     "devices" :
    //     objectType === eLynxObjectTypeEnum.Well ?
    //         "wells" :
    //         objectType === eLynxObjectTypeEnum.Tank ?
    //             'tanks' :
    //             'tankBatteries';

    // const {
    //     fields: availableObjects,
    //     //append: appendTag, //TODO when adding editing
    //     //remove: removeTag
    //     //update
    // } = useFieldArray({
    //     control: control,
    //     name: name,
    //     keyName: "fieldKey"
    // });    

    // const availableObjects = watch(name).filter(o => o.allowSelection);

    // const enableViewSelected = (availableObjects?.flatMap(o => o.tags).filter(o => o.selected)?.length) > 0;

    // const count = AlarmSetpointUsageUtils.filter(availableObjects, state.objectNameFilter, state.tagNameFilter).length;

    const enableViewSelected = fields.length > 0;

    const count = filteredObjects.length;

    function updateCheckState(checked: boolean) {

        const visibleObjects = Enumerable
            .from(filteredObjects ?? [])
            .where(o => expandedObjects.some(e => e.id === o.id));

        const visibleTags = Enumerable
            .from(visibleObjects)
            .selectMany<IEditTagSearchResultTagDTO, IEditTagDTO>(
                o => o.tags,
                (item, tag) => {
                    const newTag: IEditTagDTO = {
                        tagObjectID: item.id,
                        tagObjectName: item.name,
                        tagObjectTypeID: item.objectTypeID,
                        tagID: tag.id,
                        tagName: tag.name,
                        tatID: 0,//tag.tatID,
                        tagHidden: false,//tag.hidden,
                        tagDisabled: false,//tag.disabled,
                        notifyOnAlarm: false,//tag.notifyOnAlarm,
                        notifyOnNormal: false,//tag.notifyOnNormal,
                        tagUnitOfMeasureID: 0,//tag.tagUnitOfMeasureID,
                        tagUomIsIncompatible: false,//tag.tagUomIsIncompatible,
                        selected: checked
                    };

                    return newTag;
                }
            )
            .toArray();

        const selectedTags = fields.map(tag => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { fieldKey, ...rest } = tag;
            return rest;
        });

        if (checked) {
            const tags = Enumerable
                .from(selectedTags)
                .union(
                    visibleTags,
                    tag => tag.tagID
                )
                .toArray();

            replace(tags)
        } else {
            const tags = Enumerable
                .from(selectedTags)
                .except(
                    visibleTags,
                    tag => tag.tagID
                )
                .toArray();

            replace(tags);
        }

        // const filteredItems = AlarmSetpointUsageUtils.filter(availableObjects, state.objectNameFilter, state.tagNameFilter);

        // filteredItems.forEach(o => {

        //     const ndx = availableObjects.findIndex(v => v.id === o.id);
        //     const item = getValues(`${name}.${ndx}`);

        //     if (item && item.expanded) {

        //         o.tags.filter(v => !!contains(v.name, state.tagNameFilter) && v.selected !== checked).forEach(v => {

        //             const tagIndex = item.tags.findIndex(x => x.id === v.id);

        //             if (tagIndex !== -1) {
        //                 setValue(`${name}.${ndx}.tags.${tagIndex}.selected`, checked, { shouldDirty: true });
        //             }
        //         });
        //     }

        //     trigger();
        // });

    }

    function executeAction(action: SetpointUsageActions | BaseActionEnum) {


        switch (action) {
            case SetpointUsageActions.SelectAll:
                updateCheckState(true);
                break;

            case SetpointUsageActions.RemoveAll:
                updateCheckState(false);
                break;
        }
    }

    function renderItem(_index: number, item: IEditTagSearchResultDeviceDTO) {


        return (
            <AlarmSetpointUsageListItem
                // name={name}
                item={item}
                key={`${item.id}`}
                // openUsage={state.tagNameFilter.trim().length > 0 || state.viewSelected}
                // tagFilter={state.tagNameFilter}
                // showSelected={state.viewSelected}
                onExpandClick={toggleExpansion}
                onTagClick={(tag) => {
                    if (tag.selected) {
                        const newTag: IEditTagDTO = {
                            tagObjectID: item.id,
                            tagObjectName: item.name,
                            tagObjectTypeID: item.objectTypeID,
                            tagID: tag.id,
                            tagName: tag.name,
                            tatID: 0,//tag.tatID,
                            tagHidden: false,//tag.hidden,
                            tagDisabled: false,//tag.disabled,
                            notifyOnAlarm: false,//tag.notifyOnAlarm,
                            notifyOnNormal: false,//tag.notifyOnNormal,
                            tagUnitOfMeasureID: 0,//tag.tagUnitOfMeasureID,
                            tagUomIsIncompatible: false,//tag.tagUomIsIncompatible,
                            selected: tag.selected
                        };
                        append(newTag);
                    } else {
                        const ndx = fields.findIndex(v => v.tagID === tag.id);
                        if (ndx !== -1) {
                            remove(ndx);
                        }
                    }
                }}
            />
        );
    };

    // const noDataMessage = data?.totalCount === 0
    //     ? t("noData.noDataTypesForObject", { dataTypeName: t("dataTypeNames.alarmSetpoint"), objectName })
    //     : data?.filteredCount === 0
    //         ? t("noData.noFilteredDataTypesForObject", { dataTypeName: t("dataTypeNames.alarmSetpoint"), objectName })
    //         : undefined;

    //console.log(`tags: ${JSON.stringify(availableObjects)}`);


    function onViewSelected(value: boolean) {
        setViewSelected(value);
    }


    function onSetFilter(value: string) {
        setObjectFilter(value);
    }

    function onSetTagFilter(value: string) {
        setTagFilter(value);
    }


    // function onFilterItem(item: AlarmSetpointUsageItem): boolean {

    //     //let show = (!state.viewSelected /*|| item.selected*/) && !!item.name.match(filterRegEx);
    //     let show = !!contains(item.name, state.objectNameFilter)

    //     if (show && state.tagNameFilter.trim().length !== 0) {
    //         show = item.tags.filter(o => !!contains(o.name, state.tagNameFilter) &&
    //             (o.selected || !state.viewSelected)).length > 0;
    //     }

    //     if (show && state.viewSelected) {
    //         show = item.tags.filter(o => o.selected).length > 0;
    //     }


    //     return show;
    // }

    useEffect(() => {

        if (!enableViewSelected) {
            setViewSelected(false);
        }

    }, [
        enableViewSelected,
        setViewSelected
    ]);

    useEffect(() => {
        if (setViewOptionState) {
            setViewOptionState(state.viewOptionState);
        }
    }, [
        state.viewOptionState,
        setViewOptionState
    ]);


    return (visible ? <Stack
        direction='row'
        sx={{
            display: 'flex',
            flex: '1 1 auto',
            overflow: 'hidden'
        }}>
        <Box
            sx={{
                display: 'flex',
                flex: '1 1 auto',
                flexDirection: 'column',

                //paddingTop: 1,
                overflowY: 'auto'
            }}>
            <Box sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                overflow: "hidden"
            }}>

                {count === 0 &&
                    <Box sx={{
                        padding: 1
                    }}>
                        <NoData text={t('setpointAdmin.nodata.noFilterMatch', { name: 'objects' })} />
                    </Box>
                }

                <VirtualizedList
                    items={filteredObjects}
                    renderItem={renderItem}
                    // filter={onFilterItem}
                />

                {/* <Box
                    sx={{
                        height: 1,
                        overflowY: "auto"
                    }}
                >
                    {availableObjects
                        .filter(o => onFilterItem(o))
                        .map((o, index) => renderItem(index, o))
                    }
                </Box> */}

            </Box>
        </Box>

        <SideSheet
            title={t("options")}
            open={openSideSheet ?? false}
            hideCloseButton={!isPhone}
            onClose={() => onCloseSideSheet && onCloseSideSheet()}
            onReset={resetState}
        >

            <SideSheetSwitch

                label={t("setpointAdmin.viewSelected", { name: getObjectTypeName(objectType, true) })}
                checked={state.viewSelected}
                onChange={(checked: boolean) => {
                    onViewSelected(checked);
                    if(checked) {
                        expandAll();
                    } else if(!hasTagFilter) {
                        collapseAll();
                    }
                }}
                disabled={!enableViewSelected} />

            <SideSheetFilterInput
                label={t("setpointAdmin.filterByObject", { objectType: getObjectTypeName(objectType) })}
                value={state.objectNameFilter}
                onChange={(value) => {
                    onSetFilter(value);
                }}
                disabled={state.viewSelected} />

            <SideSheetFilterInput
                label={t("setpointAdmin.filterByObject", { objectType: getObjectTypeName(eLynxObjectTypeEnum.Tag) })}
                value={state.tagNameFilter}
                onChange={(value) => {
                    onSetTagFilter(value);
                    if (value?.length > 0) {
                        expandAll();
                    } else {
                        collapseAll();
                    }
                }}
                disabled={state.viewSelected} />

        </SideSheet>

    </Stack> : null);
}

export default forwardRef(AlarmSetpointUsageList);